import { localDelete, localGet, localPut } from 'utils/storage/local-storage';
import { DBStore, DBStoreParams, DBStoreTypes, UpsertParams } from 'utils/storage/types';

export interface ChannelDataDBValue {
  channelType: string;
  isConnected: boolean;
  channelURL: string;
}

export interface ChannelDBValue {
  channelId?: string;
  channelNickname?: string;
  channelData?: ChannelDataDBValue;
  channelLifetime?: number;
  resourceURL?: string;
  doNotDisturbAware?: boolean;
}

const Channel: DBStore<ChannelDBValue | ChannelDataDBValue | string | number | boolean> = {
  upsert({
    key,
    data,
    discriminator,
  }: UpsertParams<ChannelDBValue | ChannelDataDBValue | string | number | boolean>) {
    return new Promise((resolve, reject) => {
      const value = localGet<ChannelDBValue>({ key: DBStoreTypes.CHANNEL, discriminator });
      if (value && key) {
        if (
          (['string', 'number', 'boolean'].includes(typeof data) && key !== 'channelData') ||
          (typeof data === 'object' && key === 'channelData')
        ) {
          localPut<ChannelDBValue>({
            key: DBStoreTypes.CHANNEL,
            data: Object.assign({}, value, { [key]: data }),
            discriminator,
          });
        } else {
          reject(new Error(`Cannot insert data of type ${typeof data} for key ${key}.`));
        }
      } else {
        localPut<ChannelDBValue>({
          key: DBStoreTypes.CHANNEL,
          data: data as ChannelDBValue,
          discriminator,
        });
      }
      resolve();
    });
  },

  get({ key, discriminator }: DBStoreParams) {
    return new Promise(resolve => {
      const value = localGet<ChannelDBValue>({ key: DBStoreTypes.CHANNEL, discriminator });
      if (value && key) {
        switch (key) {
          case 'channelId':
          case 'channelNickname':
          case 'resourceURL':
            resolve(value[key] as string);
            break;
          case 'channelLifetime':
            resolve(value[key] as number);
            break;
          case 'doNotDisturbAware':
            resolve(value[key] as boolean);
            break;
          case 'channelData':
            resolve(value[key] as ChannelDataDBValue);
            break;
        }
      } else {
        resolve(value as ChannelDBValue);
      }
    });
  },

  delete({ key, discriminator }: DBStoreParams) {
    return new Promise((resolve, reject) => {
      if (key) {
        const data = localGet<ChannelDBValue>({ key: DBStoreTypes.CHANNEL, discriminator });
        if (data) {
          const result = delete data[key];
          if (!result) {
            reject(
              new Error(`Could not delete key [${key}] as it is non-existent. No changes made.`)
            );
          } else {
            localPut<ChannelDBValue>({ key: DBStoreTypes.CHANNEL, data, discriminator });
            resolve();
          }
        }
      } else {
        localDelete({ key: DBStoreTypes.CHANNEL, discriminator });
        resolve();
      }
    });
  },
};

export default Channel;
